import {Modal, Spin} from 'antd'
import {docs_editCodiDocumentRow_Api} from 'apis'
import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import {LoginContext} from 'components/LoginContext'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {RequiredSystemAdmin} from 'components/RequiredAdmin'
import {NullAsDefault, renderSelf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {flatten_rows} from './codiHelpers'
import {showDeleteConfirmDialog} from '../factory/createConfirmDialog'
import {notifyOnError} from '../factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from '../factory/createNotification'
import {bindQueryParams} from '../functions/routerHelper'
import {EditNotes, InsertNotes} from './ModifyNotes'
import {isSystemAdmin} from "../../../helpers/authUtils";

export const CodiNotesContext = React.createContext({})

export const CodiNotesProvider = ({
  row_id,
  codi_id,
  children,
  notes: data,
  onRefresh = NullAsDefault,
}) => {
  const t = useTranslate()

  const notes = Array.from(data ?? [])

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const onCancel = () => {
    setAction({})
  }

  const onError = notifyOnError(t)

  const createNoteAsync = useAsyncAction({
    onError,
    apiInfo: docs_editCodiDocumentRow_Api,
    query: bindQueryParams([{ id: codi_id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.CREATE, t)
      onCancel()
      onRefresh()
    },
  })

  const editNoteAsync = useAsyncAction({
    onError,
    apiInfo: docs_editCodiDocumentRow_Api,
    query: bindQueryParams([{ id: codi_id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      onCancel()
      onRefresh()
    },
  })

  const deleteNoteAsync = useAsyncAction({
    onError,
    apiInfo: docs_editCodiDocumentRow_Api,
    query: bindQueryParams([{ id: codi_id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      onCancel()
      onRefresh()
    },
  })

  const onCreate = (values) => {
    const { handleAsyncAction } = createNoteAsync ?? {}
    const new_notes = notes
    new_notes.push(values)
    handleAsyncAction({ notes: JSON.stringify(new_notes) })
  }

  const onUpdate = useCallback(
    (values) => {
      if (values) {
        const { id: note_id, ...note } = values ?? {}
        const { handleAsyncAction } = editNoteAsync ?? {}
        let new_notes = notes.reduce((prev, current) => {
          if (current?.key === note_id) {
            return [
              ...prev,
              {
                ...(current ?? {}),
                ...note,
              },
            ]
          } else {
            return [...prev, current]
          }
        }, [])
        handleAsyncAction({ notes: JSON.stringify(new_notes) })
      }
    },
    [notes]
  )

  const onDelete = useCallback(
    (note_id) => {
      if (note_id) {
        const { handleAsyncAction } = deleteNoteAsync ?? {}
        const new_notes = notes.filter(({ key }) => key !== note_id)
        handleAsyncAction({ notes: JSON.stringify(new_notes) })
      }
    },
    [notes]
  )

  const handleAction = useCallback(
    (action, params) => {
      switch (action) {
        case CRUD.CREATE:
        case CRUD.UPDATE:
          setAction({ type: action, value: params })
          break
        case CRUD.DELETE:
          showDeleteConfirmDialog({
            translate: t,
            onOk: () => {
              onDelete(params?.key)
            },
          })
          break
        default:
          break
      }
    },
    [action]
  )

  const modal = useMemo(() => {
    let Content = NullAsDefault
    let params = {}
    let modal_params = {}
    switch (action.type) {
      case CRUD.CREATE:
        params = {
          onSubmit: (values) => {
            onCreate(values)
          },
          isLoading: createNoteAsync?.isLoading,
        }
        modal_params = {
          title: (
              <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('create note')}
            </div>
          ),
        }
        Content = InsertNotes
        break
      case CRUD.UPDATE:
        params = {
          onSubmit: (values) => {
            onUpdate(values)
          },
          isLoading: editNoteAsync?.isLoading,
        }
        modal_params = {
          title: (
              <div className="text-center font-bold text-color-000 uppercase tracking-wide">
              {t('edit note')}
            </div>
          ),
        }
        Content = EditNotes
        break
      default:
        break
    }

    return (
      <Modal
        footer={null}
        onCancel={onCancel}
        open={action?.type}
        destroyOnHidden={true}
        className="custom-modal"
        {...modal_params}>
        <DelayRender lazy={true}>
          <Content
            {...{
              onCancel,
              item: action?.value,
            }}
            {...params}
          />
        </DelayRender>
      </Modal>
    )
  }, [action])

  return (
    <CodiNotesContext.Provider
      value={{
        row_id,
        codi_id,
        onCancel,
        handleAction,
        editNoteAsync,
        createNoteAsync,
        deleteNoteAsync,
      }}>
      <React.Fragment>
        {children}
        {modal}
      </React.Fragment>
    </CodiNotesContext.Provider>
  )
}

const NoteItem = ({ item, style, className, renderContextMenu = NullAsDefault }) => {
  const { key, text_content } = item ?? {}
  return (
    <div
      style={style}
      className={classNames('space-x-1 leading-snug relative', className)}>
      <span className="font-medium text-sm text-green-600 italic">
        {`${key} : `}
      </span>
      <span className="text-sm text-color-300">{text_content}</span>
      <RequiredSystemAdmin type={"codi_document"}>
        <div className="absolute right-0 top-0">{renderContextMenu(item)}</div>
      </RequiredSystemAdmin>
    </div>
  )
}

const CodiNotes = ({
  row,
  style,
  codi_id,
  className,
  onRefresh,
  renderContextMenu,
  render = renderSelf,
  Placeholder = EmptyHolder,
}) => {
  const login = useContext(LoginContext)
  const notes = flatten_rows(row, 'notes')
  const is_system_admin = Boolean(isSystemAdmin(login))
  if (!is_system_admin && _.isEmpty(notes)) {
    return <Placeholder />
  }
  return (
    <CodiNotesProvider
      notes={notes}
      row_id={row?.id}
      codi_id={codi_id}
      onRefresh={onRefresh}>
      {render(
        <div
          style={style}
          className={classNames('flex flex-col gap-2', className)}>
          {is_system_admin && _.isEmpty(notes) ? (
            <EmptyHolder />
          ) : (
            <CodiNotesContext.Consumer>
              {({ deleteNoteAsync }) => (
                <Spin spinning={!!deleteNoteAsync?.isLoading}>
                  <div className="space-y-2">
                    {notes.map((note, index) => (
                        <NoteItem
                            key={index}
                            item={note}
                            renderContextMenu={renderContextMenu}
                        />
                    ))}
                  </div>
                </Spin>
              )}
            </CodiNotesContext.Consumer>
          )}
        </div>
      ) ?? null}
    </CodiNotesProvider>
  )
}

export default CodiNotes
